export interface ClientDescription {
  operatingSystem: string;
  browser: string;
}

const UNKNOWN = "Unknown";

// Windows NT 10.0 is reported by both Windows 10 and 11 - Microsoft froze the
// token - so the user agent alone cannot tell them apart.
const WINDOWS_VERSIONS: Record<string, string> = {
  "10.0": "10/11",
  "6.3": "8.1",
  "6.2": "8",
  "6.1": "7",
};

const describeOperatingSystem = (ua: string): string => {
  // iOS before macOS: an iPhone's agent says "like Mac OS X" too.
  const ios = ua.match(/(?:iPhone|iPad|iPod).*? OS (\d+(?:_\d+)*)/);
  if (ios) {
    return `iOS ${ios[1].replace(/_/g, ".")}`;
  }
  // Android before Linux: every Android agent also says "Linux".
  const android = ua.match(/Android (\d+(?:\.\d+)*)/);
  if (android) {
    return `Android ${android[1]}`;
  }
  const windows = ua.match(/Windows NT (\d+\.\d+)/);
  if (windows) {
    const version = WINDOWS_VERSIONS[windows[1]];
    return version ? `Windows ${version}` : "Windows";
  }
  if (/CrOS/.test(ua)) {
    return "ChromeOS";
  }
  const mac = ua.match(/Mac OS X (\d+(?:[._]\d+)*)/);
  if (mac) {
    return `macOS ${mac[1].replace(/_/g, ".")}`;
  }
  if (/Macintosh/.test(ua)) {
    return "macOS";
  }
  if (/Linux/.test(ua)) {
    return "Linux";
  }
  return UNKNOWN;
};

// Order matters: Edge, Opera and Samsung Internet all carry a Chrome/ token,
// and Chrome carries a Safari/ one, so the more specific name is tried first.
const BROWSERS: [name: string, pattern: RegExp][] = [
  ["Edge", /Edg(?:e|A|iOS)?\/([\d.]+)/],
  ["Opera", /(?:OPR|Opera)\/([\d.]+)/],
  ["Samsung Internet", /SamsungBrowser\/([\d.]+)/],
  ["Firefox", /(?:Firefox|FxiOS)\/([\d.]+)/],
  ["Chrome", /(?:Chrome|CriOS)\/([\d.]+)/],
  // Safari's own version is in Version/; its Safari/ token is the WebKit build.
  ["Safari", /Version\/([\d.]+).*Safari\//],
];

const describeBrowser = (ua: string): string => {
  for (const [name, pattern] of BROWSERS) {
    const match = ua.match(pattern);
    if (match) {
      return `${name} ${match[1]}`;
    }
  }
  return UNKNOWN;
};

/** The OS and browser a ticket is filed from, read from a user agent. */
export const describeClient = (userAgent: string): ClientDescription => ({
  operatingSystem: describeOperatingSystem(userAgent),
  browser: describeBrowser(userAgent),
});
